import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import {
  Pagination,
  Table,
  Button,
  Segment,
  Popup,
  Loader,
  Dimmer,
  Label,
  Divider,
} from 'semantic-ui-react';
import {
  getEmployeeSlots,
  openCreateEmployeeSlotModal,
  setEmployeeActionsPage,
  setEmployeeSlotsPage,
} from 'state/business/actions';
import CreateEmployeeSlotConfirmationModal from './CreateEmployeeSlotConfirmationModal';

const EmployeeSlots = props => {
  const {
    employeeSlots,
    employeeSlotsPage,
    allBusiness,
    creatingEmployeeSlot,
    gettingEmployeeSlots,
    dispatch,
  } = props;

  useEffect(() => {
    const businessId = allBusiness.data[allBusiness.activeIndex].id;
    dispatch(setEmployeeSlotsPage(1));
    dispatch(setEmployeeActionsPage(1));
    dispatch(getEmployeeSlots({ businessId }));
  }, [allBusiness.activeIndex, allBusiness.data, dispatch]);

  const size = 10;
  let totalPages = 1;
  if (employeeSlots.paging)
    totalPages = Math.ceil(employeeSlots.paging.total / size);

  const handleOnPageChange = (_, val) => {
    const { activePage } = val;
    dispatch(setEmployeeSlotsPage(activePage));
  };

  const handleOnAddEmployeeButtonClick = () => {
    dispatch(openCreateEmployeeSlotModal());
  };

  return (
    <>
      <CreateEmployeeSlotConfirmationModal />
      <Dimmer active={creatingEmployeeSlot || gettingEmployeeSlots}>
        <Loader size="massive">Loading</Loader>
      </Dimmer>
      {employeeSlots.data ? (
        <>
          <Label>
            Used
            <Label.Detail>
              {employeeSlots.data.total_used_slots} /{' '}
              {employeeSlots.data.total_slots}
            </Label.Detail>
          </Label>
          <Label>
            Free
            <Label.Detail>{employeeSlots.data.total_free_slots}</Label.Detail>
          </Label>
          <Label>
            Paid
            <Label.Detail>
              {employeeSlots.data.total_purchased_slots}
            </Label.Detail>
          </Label>
          <Divider style={{ margin: '8px 0px 8px 0px' }} />
          <Segment
            textAlign="right"
            basic
            style={{ margin: '8px 0px 8px 0px', padding: '0px 0px 0px 0px' }}
          >
            {new Date(
              allBusiness.data[allBusiness.activeIndex].expired_at
            ).getTime() <= Date.now() ? (
              <Popup
                content="Business is expired"
                trigger={
                  <span>
                    <Button color="blue" disabled content="Add Employee Slot" />
                  </span>
                }
              />
            ) : (
              <Button color="blue" onClick={handleOnAddEmployeeButtonClick}>
                Add Employee Slot
              </Button>
            )}
          </Segment>
        </>
      ) : (
        <></>
      )}
      <div
        style={{
          'overflow-x': 'scroll',
        }}
      >
        <Table celled>
          <Table.Header>
            <Table.Row textAlign="center">
              <Table.HeaderCell>User ID</Table.HeaderCell>
              <Table.HeaderCell>Email</Table.HeaderCell>
              <Table.HeaderCell>Name</Table.HeaderCell>
              <Table.HeaderCell>Type</Table.HeaderCell>
              <Table.HeaderCell>Created At</Table.HeaderCell>
              <Table.HeaderCell>Expiration Date</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {employeeSlots.data &&
            new Date(
              allBusiness.data[allBusiness.activeIndex].expired_at
            ).getTime() > Date.now() ? (
              employeeSlots.data.slots
                .slice((employeeSlotsPage - 1) * size, employeeSlotsPage * size)
                .map(employee => {
                  let keys = '';
                  Object.keys(employee).forEach(key => {
                    keys += employee[key];
                  });
                  return (
                    <Table.Row textAlign="center" key={`${keys}slots`}>
                      {employee.employee ? (
                        <>
                          <Table.Cell key={`${keys}userId`}>
                            {employee.employee.id}
                          </Table.Cell>
                          <Table.Cell key={`${keys}email`}>
                            {employee.employee.email}
                          </Table.Cell>
                          <Table.Cell key={`${keys}staffSlot`}>
                            {`${employee.employee.first_name} ${employee.employee.last_name}`}
                          </Table.Cell>
                        </>
                      ) : (
                        <>
                          <Table.Cell key={`${keys}id`}>-</Table.Cell>
                          <Table.Cell key={`${keys}email`}>-</Table.Cell>
                          <Table.Cell key={`${keys}staffSlot`}>-</Table.Cell>
                        </>
                      )}
                      <Table.Cell key={`${keys}type`}>
                        {employee.status}
                      </Table.Cell>
                      <Table.Cell key={`${keys}createdAt`}>
                        {format(new Date(employee.created_at), 'dd/MM/yyyy')}
                      </Table.Cell>
                      <Table.Cell key={`${keys}expirationDate`}>
                        {format(new Date(employee.expire_date), 'dd/MM/yyyy')}
                      </Table.Cell>
                      <Table.Cell
                        key={`${keys}status`}
                        style={{
                          color: employee.is_renewable ? 'green' : 'red',
                        }}
                      >
                        {employee.is_renewable ? 'Active' : 'Not Renewed'}
                      </Table.Cell>
                    </Table.Row>
                  );
                })
            ) : (
              <></>
            )}
          </Table.Body>
          {employeeSlots.paging ? (
            <Table.Footer>
              <Table.Row>
                <Table.HeaderCell colSpan="7">
                  <Pagination
                    activePage={employeeSlotsPage}
                    totalPages={totalPages}
                    onPageChange={handleOnPageChange}
                    size="mini"
                  />
                </Table.HeaderCell>
              </Table.Row>
            </Table.Footer>
          ) : (
            <></>
          )}
        </Table>
      </div>
    </>
  );
};

const mapStateToProps = ({ business }) => ({
  employeeSlotsPage: business.employeeSlotsPage,
  creatingEmployeeSlot: business.creatingEmployeeSlot,
  employeeSlots: business.employeeSlots,
  allBusiness: business.allBusiness,
  gettingEmployeeSlots: business.gettingEmployeeSlots,
});

EmployeeSlots.propTypes = {
  dispatch: PropTypes.func.isRequired,
  employeeSlotsPage: PropTypes.number.isRequired,
  employeeSlots: PropTypes.object.isRequired,
  allBusiness: PropTypes.object.isRequired,
  creatingEmployeeSlot: PropTypes.bool.isRequired,
  gettingEmployeeSlots: PropTypes.bool.isRequired,
};

export default connect(mapStateToProps)(EmployeeSlots);
